import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import DashboardLayout from "../components/DashboardLayout";
import api from "../lib/api";
import CountUp from "../components/CountUp";
import { Trophy, Crown, Medal, Sparkles, Loader2, ArrowUpRight } from "lucide-react";

export default function LeaderboardPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [defs, setDefs] = useState(null);

  useEffect(() => {
    api.get("/passport/leaderboard/global")
      .then((r) => setRows(r.data || []))
      .finally(() => setLoading(false));
    api.get("/passport/skills").then((r) => setDefs(r.data));
  }, []);

  const top3 = rows.slice(0, 3);
  const rest = rows.slice(3);

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto pb-16">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
              Global Leaderboard
            </p>
            <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tighter text-[#0A192F]">
              The world's <span className="font-serif italic text-[#0F4C81]">top diplomats</span>.
            </h1>
            <p className="mt-2 text-slate-500 text-sm">
              Live ranking across every Diplomexa conference, course and award.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="py-20 text-center">
            <Loader2 className="h-6 w-6 text-[#0F4C81] animate-spin mx-auto" />
            <p className="mt-3 text-sm text-slate-500">Loading leaderboard…</p>
          </div>
        ) : (
          <>
            {/* Podium */}
            <div className="mt-10 grid md:grid-cols-3 gap-4" data-testid="leaderboard-podium">
              {[1, 0, 2].map((i, slot) => {
                const r = top3[i];
                if (!r) return <div key={i} className="hidden md:block" />;
                const heights = { 0: "lg:h-72", 1: "lg:h-60", 2: "lg:h-56" };
                const icons = { 0: Crown, 1: Trophy, 2: Medal };
                const Icon = icons[i];
                const colors = {
                  0: "bg-gradient-to-br from-[#D4AF37] via-[#f6d272] to-[#D4AF37] text-[#0A192F] shadow-[0_20px_60px_-15px_rgba(212,175,55,0.6)]",
                  1: "bg-gradient-to-br from-slate-200 via-slate-300 to-slate-400 text-[#0A192F] shadow-[0_18px_50px_-15px_rgba(100,116,139,0.5)]",
                  2: "bg-gradient-to-br from-orange-400 via-orange-500 to-orange-600 text-white shadow-[0_18px_50px_-15px_rgba(234,88,12,0.55)]",
                };
                const sparkleColors = { 0: "#ffffff", 1: "#ffffff", 2: "#fde68a" };
                return (
                  <motion.div
                    key={r.delegate_id}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: slot * 0.12, type: "spring", stiffness: 220, damping: 20 }}
                    whileHover={{ y: -4 }}
                    className={`relative overflow-hidden ${heights[i]} rounded-2xl p-6 flex flex-col justify-end ${colors[i]}`}
                    data-testid={`podium-${i + 1}`}
                  >
                    {/* Floating sparkle accents — only on #1 */}
                    {i === 0 && (
                      <>
                        <motion.span
                          animate={{ y: [-4, 4, -4], opacity: [0.6, 1, 0.6] }}
                          transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                          className="absolute top-5 right-6"
                        >
                          <Sparkles className="h-4 w-4" style={{ color: sparkleColors[i] }} />
                        </motion.span>
                        <motion.span
                          animate={{ y: [3, -3, 3], opacity: [0.4, 0.9, 0.4] }}
                          transition={{ duration: 3.1, repeat: Infinity, ease: "easeInOut", delay: 0.4 }}
                          className="absolute top-10 right-12"
                        >
                          <Sparkles className="h-3 w-3" style={{ color: sparkleColors[i] }} />
                        </motion.span>
                      </>
                    )}
                    <motion.div
                      animate={i === 0 ? { rotate: [0, -8, 8, 0] } : {}}
                      transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                    >
                      <Icon className="h-9 w-9 mb-3 drop-shadow-md" />
                    </motion.div>
                    <p className="text-[10px] uppercase tracking-[0.22em] font-bold opacity-80">
                      Rank #{i + 1}
                    </p>
                    <h3 className="mt-2 text-xl font-bold tracking-tight">{r.name}</h3>
                    <p className="mt-1 text-xs opacity-80">{r.organisation || "Independent"}</p>
                    <div className="mt-4 flex items-center justify-between">
                      <span className="font-serif text-2xl">
                        <CountUp
                          to={r.points}
                          duration={1.4 + slot * 0.2}
                          sparkle={i === 0}
                          milestoneEvery={50}
                          milestoneMin={50}
                          testid={`podium-points-${i + 1}`}
                        />
                        <span className="ml-1 text-xs font-sans opacity-70 tracking-widest">PTS</span>
                      </span>
                      <span className="px-2 py-1 rounded-full bg-white/30 backdrop-blur-sm text-[10px] uppercase tracking-[0.18em] font-bold">
                        {r.level.label.replace(" Delegate", "").replace(" Global Leader", "").replace(" Youth Diplomat", "")}
                      </span>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Full table */}
            <div className="lift-card mt-10 rounded-2xl bg-white border border-slate-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                <p className="text-sm font-semibold text-[#0A192F]">All ranked delegates</p>
                <span className="text-xs text-slate-500">{rows.length} delegates</span>
              </div>
              <table className="w-full text-sm" data-testid="leaderboard-table">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-slate-500 bg-slate-50">
                    <th className="px-6 py-3 font-semibold">#</th>
                    <th className="px-6 py-3 font-semibold">Delegate</th>
                    <th className="px-6 py-3 font-semibold">Organisation</th>
                    <th className="px-6 py-3 font-semibold">Level</th>
                    <th className="px-6 py-3 font-semibold text-right">Points</th>
                    <th className="px-6 py-3 font-semibold"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rest.map((r, idx) => {
                    const isTop10 = idx + 4 <= 10;
                    return (
                      <motion.tr
                        key={r.delegate_id}
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: Math.min(idx * 0.02, 0.4) }}
                        className="group hover:bg-gradient-to-r hover:from-[#D4AF37]/5 hover:to-transparent transition-colors"
                      >
                        <td className="px-6 py-3.5 text-slate-500 font-mono">
                          <span className={`inline-flex items-center justify-center h-6 w-6 rounded-full text-[10px] font-bold ${
                            isTop10
                              ? "bg-[#D4AF37]/15 text-[#0A192F] border border-[#D4AF37]/40"
                              : "text-slate-500"
                          }`}>
                            {idx + 4}
                          </span>
                        </td>
                        <td className="px-6 py-3.5">
                          <p className="font-semibold text-[#0A192F] group-hover:text-[#0F4C81] transition-colors">{r.name}</p>
                        </td>
                        <td className="px-6 py-3.5 text-slate-600">{r.organisation || "—"}</td>
                        <td className="px-6 py-3.5">
                          <span
                            className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.18em] font-bold"
                            style={{ backgroundColor: `${r.level.color}20`, color: r.level.color }}
                          >
                            {r.level.label}
                          </span>
                        </td>
                        <td className="px-6 py-3.5 text-right font-mono font-bold text-[#0F4C81] tabular-nums">
                          <CountUp
                            to={r.points}
                            duration={0.9}
                            sparkle={false}
                            testid={`leaderboard-points-${r.delegate_id}`}
                          />
                        </td>
                        <td className="px-6 py-3.5 text-right">
                          <Link
                            to={`/dashboard/passport/${r.delegate_id}`}
                            className="text-[10px] uppercase tracking-[0.18em] text-slate-500 hover:text-[#0F4C81] inline-flex items-center gap-1 opacity-60 group-hover:opacity-100 transition-opacity"
                            data-testid={`view-passport-${r.delegate_id}`}
                          >
                            View <ArrowUpRight className="h-3 w-3" />
                          </Link>
                        </td>
                      </motion.tr>
                    );
                  })}
                  {rest.length === 0 && rows.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-6 py-10 text-center text-slate-500">
                        No delegates have earned points yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Levels legend */}
            {defs?.levels && (
              <div className="mt-8 rounded-2xl bg-[#0A192F] text-white p-6">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
                  <Sparkles className="h-3 w-3" /> Passport tiers
                </div>
                <h2 className="mt-2 text-xl font-semibold tracking-tight">
                  Five levels. One global journey.
                </h2>
                <div className="mt-5 grid sm:grid-cols-3 lg:grid-cols-5 gap-3">
                  {defs.levels.map((lvl) => (
                    <div key={lvl.key} className="rounded-lg p-4 border border-white/10 bg-white/5">
                      <span
                        className="inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.18em] font-bold"
                        style={{ backgroundColor: lvl.color, color: "#0A192F" }}
                      >
                        {lvl.label}
                      </span>
                      <p className="mt-2 text-xs text-slate-400">
                        {lvl.min}–{lvl.max === 99999 ? "∞" : lvl.max} pts
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
